import { platformSlaIdToServiceLevelName } from '@provision/service-level-utils';
import dayjs from 'dayjs';
import { ReactNode } from 'react';
import { DISPLAY_LONG_DATE_FORMAT } from 'src/constants/date';
import {
  ActivityFeed,
  ActivityFeedChange,
} from 'src/graphql/entities/activity-feed/hooks/use-activity-feed';
// Imported from the module rather than the `service-level` barrel: the barrel also pulls in
// `useServiceLevels`, whose Apollo/store chain reaches the Navbar and crashes this file's test.
import { formatServiceLevelWithCpp } from 'src/graphql/entities/service-level/utils';
import { SUPPRESS_ACTIVITY_ROW } from './suppress-activity-row';

export function getCustomAddressFieldMessage(
  change: ActivityFeedChange,
  activity: ActivityFeed,
): ReactNode | typeof SUPPRESS_ACTIVITY_ROW {
  const actorName = activity.actor?.name?.trim() || '<Platform User>';

  switch (change.field) {
    case 'tosAccepted':
      return change.value?.toLowerCase() === 'yes'
        ? 'The terms of Service was accepted.'
        : 'The terms of Service was changed to not accepted.';
    case 'orgNotes':
      return `${actorName} updated the site notes.`;
    case 'membershipSalesNotes':
      return `${actorName} updated the membership notes.`;
    case 'platformSlaId': {
      // Granting or expiring a complimentary period writes cppExpDate and platformSlaId in one
      // update; that branch renders both as a single sentence, so this row would duplicate it.
      if (activity.changes.some(({ field }) => field === 'cppExpDate')) {
        return SUPPRESS_ACTIVITY_ROW;
      }

      return (
        <>
          {actorName} changed Service Level from{' '}
          <b>{platformSlaIdToServiceLevelName(change.previousValue)}</b> to{' '}
          <b>{platformSlaIdToServiceLevelName(change.value)}</b>.
        </>
      );
    }
    case 'cppExpDate': {
      const cppExpiration = activity.changes.find((change) => change.field === 'cppExpDate');
      const platformSlaIdChange = activity.changes.find(
        (change) => change.field === 'platformSlaId',
      );

      const serviceLevelPreviousValue = platformSlaIdToServiceLevelName(
        platformSlaIdChange?.previousValue,
      );

      if (!cppExpiration) {
        return null;
      }
      const cppExpDate = dayjs(cppExpiration.value);
      const serviceLevelName = platformSlaIdToServiceLevelName(platformSlaIdChange?.value);

      if (cppExpDate.isSame(dayjs(), 'date') && !!serviceLevelName) {
        // The complimentary period ends today, so the tier it falls back to carries no CPP prefix.
        return `Complimentary Membership Expired. Service Level changed to ${serviceLevelName}`;
      }

      // A still-running complimentary period surfaces as the "CPP <tier>" variant.
      const serviceLevelValue = serviceLevelName
        ? formatServiceLevelWithCpp(serviceLevelName, cppExpiration.value)
        : undefined;

      if (serviceLevelValue !== serviceLevelPreviousValue) {
        return (
          <>
            {actorName} changed Service Level from <b>{serviceLevelPreviousValue}</b> to{' '}
            <b>{serviceLevelValue}</b> ({'expires '}
            {cppExpDate.format(DISPLAY_LONG_DATE_FORMAT)})
          </>
        );
      }
      return `${actorName} updated the complimentary membership to expire on ${cppExpDate.format(DISPLAY_LONG_DATE_FORMAT)}`;
    }
    case 'deletedAt': {
      const isRestored = change.previousValue !== null && change.value === null;
      if (isRestored) {
        return `${actorName} restored the site`;
      }
      // A soft delete has its own `delete` activity, so the raw timestamp must not render here.
      return SUPPRESS_ACTIVITY_ROW;
    }
    default:
      return null;
  }
}
